import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import { DirectionsCar, LocationOn, MyLocation } from '@mui/icons-material';
import GoogleMapPanel from '../../components/maps/GoogleMapPanel';
import { env } from '../../config/env';

interface PublicTrackPayload {
  rideNumber?: string;
  status: string;
  statusLabel: string;
  pickup?: { address?: string; lat?: number; lng?: number };
  dropoff?: { address?: string; lat?: number; lng?: number };
  driver?: { firstName?: string };
  driverLocation?: { lat: number; lng: number };
  expiresAt?: string;
  updatedAt?: string;
}

async function fetchTrack(token: string): Promise<PublicTrackPayload> {
  const base = env.apiUrl.replace(/\/$/, '');
  const res = await fetch(`${base}/public/track/${token}`);
  const body = await res.json();
  if (!res.ok) {
    throw new Error(body?.message || 'Unable to load trip');
  }
  return body.data as PublicTrackPayload;
}

export default function TrackTripPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<PublicTrackPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const load = async () => {
      try {
        const next = await fetchTrack(token);
        if (!cancelled) {
          setData(next);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Unable to load trip');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    const timer = setInterval(load, 12_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [token]);

  const markers = useMemo(() => {
    if (!data) return [];
    const list: { lat: number; lng: number; title?: string; color?: string }[] = [];
    if (data.pickup?.lat != null && data.pickup.lng != null) {
      list.push({ lat: data.pickup.lat, lng: data.pickup.lng, title: 'Pickup', color: '#22c55e' });
    }
    if (data.dropoff?.lat != null && data.dropoff.lng != null) {
      list.push({ lat: data.dropoff.lat, lng: data.dropoff.lng, title: 'Drop', color: '#ef4444' });
    }
    if (data.driverLocation) {
      list.push({
        lat: data.driverLocation.lat,
        lng: data.driverLocation.lng,
        title: data.driver?.firstName ? `Driver ${data.driver.firstName}` : 'Driver',
        color: '#2563eb',
      });
    }
    return list;
  }, [data]);

  const mapCenter = useMemo(() => {
    if (data?.driverLocation) return data.driverLocation;
    if (data?.pickup?.lat != null && data.pickup.lng != null) {
      return { lat: data.pickup.lat, lng: data.pickup.lng };
    }
    return markers[0] || undefined;
  }, [data, markers]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0f172a', py: { xs: 2, md: 4 } }}>
      <Container maxWidth="sm">
        <Stack spacing={2}>
          <Box textAlign="center" sx={{ color: '#fff' }}>
            <DirectionsCar sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h5" fontWeight={800}>Live trip tracking</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Shared view — location updates every few seconds
            </Typography>
          </Box>

          {loading && !data && (
            <Stack alignItems="center" py={6}>
              <CircularProgress sx={{ color: '#fff' }} />
            </Stack>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {data && (
            <>
              <Card>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="h6" fontWeight={800}>
                      {data.rideNumber ? `#${data.rideNumber}` : 'Your trip'}
                    </Typography>
                    <Chip label={data.statusLabel} color={data.status === 'completed' ? 'success' : 'primary'} />
                  </Stack>

                  {data.driver?.firstName && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Driver: {data.driver.firstName}
                    </Typography>
                  )}

                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <LocationOn fontSize="small" color="success" sx={{ mt: 0.3 }} />
                      <Typography variant="body2">{data.pickup?.address ?? 'Pickup'}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <MyLocation fontSize="small" color="error" sx={{ mt: 0.3 }} />
                      <Typography variant="body2">{data.dropoff?.address ?? 'Drop'}</Typography>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              <Card>
                <GoogleMapPanel center={mapCenter} zoom={14} height={280} markers={markers} />
              </Card>

              <Typography variant="caption" color="rgba(255,255,255,0.55)" textAlign="center">
                For your safety, exact addresses may be partially hidden. Link expires{' '}
                {data.expiresAt ? new Date(data.expiresAt).toLocaleString() : 'after the trip'}.
              </Typography>
            </>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
